import {
  type Home,
  type ResidentBody,
  type ResidentItem,
  UserRole
} from '@lmc/ts-common'
import { Button, EmptyState, Form, PageHeader } from '@lmc/ui-kit'
import { AdjustmentCategoryEnum, ImpairmentEnum } from '@lmc/validations'
import { Form as AntdForm } from 'antd'
import type { RcFile } from 'antd/lib/upload'
import { getNominative, showToast } from 'common/utils'
import { useRoleAccess } from 'components/AccessControl'
import { Spinner } from 'components/generic'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import dayjs from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useHomeType from 'hooks/useHomeType'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import useResident, { convertFormTypeToResidentBody } from 'hooks/useResident'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import {
  cleanNHSFormattedNumber,
  getNHSFormattedNumber,
  handleOnFinishFailed,
  scrollToTop
} from 'screens/Residents/screens/ResidentProfileScreen/utils/utils'
import {
  ButtonsWrapper,
  ProfileContent,
  ProfileWrapper
} from '../../styledComponents'
import { AllergiesFormCard } from './FormCards/Allergies'
import { DnacprFormCard } from './FormCards/Dnacpr'
import { FundingSourceFormCard } from './FormCards/FundingSource'
import { GeneralInfoFormCard } from './FormCards/GeneralInfo'
import { LpsFormCard } from './FormCards/Lps'
import { MedicationsFormCard } from './FormCards/Medications'
import { MentalHealthActFormCard } from './FormCards/MentalHealthAct'
import OtherIdentifierCard from './FormCards/OtherIdentifier'
import { ReasonableAdjustmentsFormCard } from './FormCards/ReasonableAdjustments'

type FormReasonableAdjustment = {
  adjustmentCategory?: string[]
  adjustmentSubcategory?: Partial<Record<string, string[]>>
  details?: string
  impairment?: string[]
}

type FormType = {
  admissionDate?: dayjs.Dayjs
  dnacprForm?: string | null
  dnacprReviewDate?: dayjs.Dayjs | null
  dolsApplicationDate?: dayjs.Dayjs
  dolsEndDate?: dayjs.Dayjs
  dolsReviewDate?: dayjs.Dayjs
  dolsStartDate?: dayjs.Dayjs
  nhsNumber?: string
  reasonableAdjustments?: FormReasonableAdjustment[]
} & Pick<
  ResidentBody,
  | 'dnacprDecisionMadeDate'
  | 'dnacprStartDate'
  | 'dolsStatus'
  | 'mhaStatus'
  | 'mhaType'
  | 'otherIdentifiers'
  | 'residentAllergies'
>

export type ResidentCareInformationFormType = FormType

const ResidentCareInformation = ({ testId }: { testId?: string } = {}) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const [form] = AntdForm.useForm()
  const navigate = useNavigate()
  const topRef = useRef<HTMLDivElement | null>(null)
  const { id } = useParams()
  const [isDirty, setIsDirty] = useState(false)
  const [selectedFile, setSelectedFile] = useState<RcFile>()
  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )
  const { hasRota } = useHomeType()

  const flags = useFeatureFlags({
    flagNames: ['reasonable_adjustments', 'mental_capacity']
  })?.data
  const reasonableAdjustmentsEnabled = flags?.reasonable_adjustments?.enabled
  const mentalCapacityEnabled = flags?.mental_capacity?.enabled

  const [isUpdateLoading, setIsUpdateLoading] = useState(false)

  const residentNominative = getNominative(
    preferredPeopleNominative,
    'Resident'
  )
  const { resident, isLoading, updateResident, updateResidentFormData } =
    useResident({
      id,
      onUpdateSuccess: () => {
        showToast({
          title: t(supConstants.RESIDENT_UPDATED, {
            variable: residentNominative
          })
        })
        navigate(`../profile`, { relative: 'path' })
      }
    })
  const { isPro, isEmar, isDoLSOverride, isFromCanada } = useHomeType()
  const sendMixpanelEvent = useMixpanelEvent()

  const convertFormTypeToPartialResidentBody = (
    values: FormType
  ): Partial<ResidentBody> => ({
    ...values,
    dnacprReviewDate: values?.dnacprReviewDate?.toISOString() || null,
    admissionDate: values?.admissionDate?.format('YYYY-MM-DDTHH:mm:ss'),
    dolsApplicationDate: values?.dolsApplicationDate?.toISOString() || '',
    dolsReviewDate: values?.dolsReviewDate?.toISOString() || '',
    dolsStartDate: values?.dolsStartDate?.toISOString() || '',
    dolsEndDate: values?.dolsEndDate?.endOf('day').toISOString() || '',
    nhsNumber: cleanNHSFormattedNumber(values?.nhsNumber),
    dnacprForm: values?.dnacprForm ? undefined : null,
    mhaType: values.mhaStatus ? values.mhaType : '',
    // Filter out null/undefined items that Ant Design Form.List can produce
    // when add() is called without arguments and the form is submitted unfilled
    otherIdentifiers: values.otherIdentifiers?.filter((item) => item != null),
    reasonableAdjustments: values.reasonableAdjustments?.map((adj) => {
      const selectedCategories = new Set(adj.adjustmentCategory ?? [])
      return {
        ...adj,
        adjustmentSubcategory: (() => {
          if (!adj.adjustmentSubcategory) return undefined
          const cleaned = Object.fromEntries(
            Object.entries(adj.adjustmentSubcategory).filter(
              ([key, v]) =>
                selectedCategories.has(key) && Array.isArray(v) && v.length > 0
            )
          )
          return Object.keys(cleaned).length > 0 ? cleaned : undefined
        })()
      }
    }) as any
  })
  const handleOnFinish = async (values: FormType) => {
    setIsUpdateLoading(true)
    const formData = new FormData()
    if (selectedFile) {
      formData.append('dnacprFile', selectedFile, selectedFile.name)
    }
    try {
      const convertedValues = convertFormTypeToResidentBody(values, (vals) =>
        convertFormTypeToPartialResidentBody(vals)
      )
      setIsDirty(false)
      const response = await updateResident(convertedValues)
      if (selectedFile) {
        await updateResidentFormData(formData)
      }

      if (!(response && typeof response === 'object' && 'result' in response)) {
        setIsUpdateLoading(false)
        return
      }

      const adjustments =
        (
          convertedValues as {
            reasonableAdjustments?: FormReasonableAdjustment[]
          }
        ).reasonableAdjustments ?? []
      const previousAdjustments = resident?.reasonableAdjustments ?? []
      const hasChanged =
        JSON.stringify(adjustments) !== JSON.stringify(previousAdjustments)

      if (hasChanged) {
        const isAdding = adjustments.length > previousAdjustments.length
        const isDeleting = adjustments.length < previousAdjustments.length

        const event = isAdding
          ? MixpanelEvents.REASONABLE_ADJUSTMENT_ADDED
          : isDeleting
            ? MixpanelEvents.REASONABLE_ADJUSTMENT_DELETED
            : MixpanelEvents.REASONABLE_ADJUSTMENT_EDITED

        // For delete, report what was removed, for add/edit, report current state
        const reportedAdjustments = isDeleting
          ? previousAdjustments
          : adjustments

        const categoryCodeToKey = Object.fromEntries(
          Object.entries(AdjustmentCategoryEnum).map(([key, code]) => [
            code,
            key
          ])
        )
        const impairmentCodeToKey = Object.fromEntries(
          Object.entries(ImpairmentEnum).map(([key, code]) => [code, key])
        )

        const mapAdjustments = (items: typeof reportedAdjustments) =>
          items.map((adjustment) => ({
            categories: (adjustment.adjustmentCategory ?? []).map(
              (code) => categoryCodeToKey[code] ?? code
            ),
            categoryCount: adjustment.adjustmentCategory?.length ?? 0,
            impairments: (adjustment.impairment ?? []).map(
              (code) => impairmentCodeToKey[code] ?? code
            ),
            impairmentCount: adjustment.impairment?.length ?? 0
          }))

        sendMixpanelEvent(event, {
          totalAdjustments: reportedAdjustments.length,
          adjustments: mapAdjustments(reportedAdjustments)
        })
      }

      setIsUpdateLoading(false)
    } catch {
      showToast({
        title: t(generalConstants.ERROR, { ns: NAMESPACE.GENERAL }),
        type: 'error'
      })
      setIsUpdateLoading(false)
    }
  }

  const handleOnCancel = () => {
    navigate(`../profile`, { relative: 'path' })
  }

  const handleFormValuesChange = (values: FormType) => {
    if (values) {
      setIsDirty(true)
    }
  }

  useEffect(() => {
    scrollToTop(topRef)
  }, [])

  useEffect(() => {
    if (!resident) {
      return
    }

    const updatedInitialValues = getInitialValues(resident, isFromCanada)
    form.setFieldsValue(updatedInitialValues)
    // we use ID as dependencies instead of resident
    // if we use resident, when we focus on window, the resident will refetch, and it will change
  }, [resident?.id])

  const hasRolePermission = useRoleAccess(
    [UserRole.careHomeAdmin],
    [
      {
        i: 'update',
        this: 'Resident'
      }
    ]
  )

  const canDeleteNhsNumber = useRoleAccess([UserRole.careHomeAdmin])

  if (isLoading) {
    return (
      <Spinner
        data-testid={
          testId ? `${testId}-care-info-loading` : 'care-info-loading'
        }
      />
    )
  }

  if (resident === undefined) {
    return (
      <EmptyState
        title={t(supConstants.NO_RESIDENT)}
        data-testid={
          testId ? `${testId}-care-info-no-resident` : 'care-info-no-resident'
        }
      />
    )
  }

  const initialValues: Partial<FormType> = getInitialValues(
    resident,
    isFromCanada
  )

  return (
    <ProfileWrapper
      ref={topRef}
      data-testid={testId ? `${testId}-care-info-wrapper` : 'care-info-wrapper'}
    >
      <PageHeader
        sticky
        title={t(supConstants.CARE_INFORMATION)}
        data-testid={testId ? `${testId}-care-info-header` : 'care-info-header'}
      >
        <ButtonsWrapper
          data-testid={
            testId ? `${testId}-care-info-buttons` : 'care-info-buttons'
          }
        >
          <Button
            data-cy="cancel-btn"
            onClick={handleOnCancel}
            data-testid={
              testId ? `${testId}-care-info-cancel-btn` : 'care-info-cancel-btn'
            }
          >
            {t(supConstants.CANCEL)}
          </Button>
          <Button
            data-cy="submit-btn"
            disabled={isUpdateLoading}
            variant="primary"
            onClick={form.submit}
            type="submit"
            isLoading={isUpdateLoading}
            disabledByPermission={!hasRolePermission}
            data-testid={
              testId ? `${testId}-care-info-save-btn` : 'care-info-save-btn'
            }
          >
            {t(supConstants.SAVE)}
          </Button>
        </ButtonsWrapper>
      </PageHeader>
      <ProfileContent
        data-testid={
          testId ? `${testId}-care-info-content` : 'care-info-content'
        }
      >
        <NavigationModal canNavigate={!isDirty} />
        <Form<FormType>
          form={form}
          onFinish={handleOnFinish}
          onValuesChange={handleFormValuesChange}
          initialValues={initialValues}
          onFinishFailed={({ errorFields }) =>
            handleOnFinishFailed(errorFields)
          }
          data-testid={testId ? `${testId}-care-info-form` : 'care-info-form'}
        >
          {isPro && (
            <>
              <DnacprFormCard
                form={form}
                dnacprValue={resident.dnacpr}
                dnacprFormUrl={resident.dnacprForm}
                setSelectedFile={setSelectedFile}
              />
              <LpsFormCard
                form={form}
                dols={resident.dols || false}
                isDoLSOverride={isDoLSOverride}
              />
              <MentalHealthActFormCard form={form} />
              <AllergiesFormCard form={form} />
              {reasonableAdjustmentsEnabled && (
                <ReasonableAdjustmentsFormCard form={form} />
              )}
              <GeneralInfoFormCard
                resident={resident}
                isFromCanada={isFromCanada}
                canDeleteNhsNumber={canDeleteNhsNumber}
                isAdmin={hasRolePermission}
                preferredTerm={residentNominative}
                mentalCapacityEnabled={mentalCapacityEnabled}
              />
              {(!hasRota || !isPro) && <FundingSourceFormCard />}
            </>
          )}
          {isPro && <OtherIdentifierCard />}
          {isEmar && <MedicationsFormCard />}
        </Form>
      </ProfileContent>
    </ProfileWrapper>
  )
}

const getInitialValues = (
  resident: ResidentItem,
  isFromCanada?: boolean
): Partial<FormType> => {
  const initialValues: Partial<FormType> = {
    ...resident,
    dnacprReviewDate: resident?.dnacprReviewDate
      ? dayjs(resident?.dnacprReviewDate)
      : null,
    admissionDate: resident?.admissionDate
      ? dayjs(resident.admissionDate.replace('Z', ''))
      : undefined,
    dolsApplicationDate: resident?.dolsApplicationDate
      ? dayjs(resident.dolsApplicationDate)
      : undefined,
    dolsReviewDate: resident?.dolsReviewDate
      ? dayjs(resident.dolsReviewDate)
      : undefined,
    dolsStartDate: resident?.dolsStartDate
      ? dayjs(resident.dolsStartDate)
      : undefined,
    dolsEndDate: resident?.dolsEndDate
      ? dayjs(resident.dolsEndDate)
      : undefined,
    nhsNumber: resident?.nhsNumber
      ? getNHSFormattedNumber(resident?.nhsNumber, isFromCanada)
      : undefined,
    mhaStatus: resident?.mhaStatus ?? false
  }

  return initialValues
}

export default ResidentCareInformation
